import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import type { PayerMixResponse } from './types';

const PAYER_MIX_TAG = 'PayerMix';
const PAYER_MIX_REIMBURSEMENT_TAG = 'PayerMixReimbursement';

// Types
interface GetPayerMixParams {
  providerId: string;
  queryParams: string;
}

interface GetPayerMixReimbursementParams {
  providerId: string;
  queryParams: string;
}

/**
 * RTK Query API for payer mix related endpoints
 */
export const payerMixApi = createApi({
  reducerPath: 'payerMixApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [PAYER_MIX_TAG, PAYER_MIX_REIMBURSEMENT_TAG],
  endpoints: (builder) => ({
    /**
     * Query: Get payer mix data for a provider (V2 endpoint)
     * Returns payer mix breakdown by type or name with filter support
     */
    getPayerMixV2: builder.query<PayerMixResponse, GetPayerMixParams>({
      query: ({ providerId, queryParams }) => ({
        url: `v1/entities/entity/${providerId}/payer-mix/${queryParams}`,
        method: 'GET',
      }),
      providesTags: (result, error, { providerId, queryParams }) => [
        { type: PAYER_MIX_TAG, id: `${providerId}-${queryParams}` },
      ],
    }),

    /**
     * Query: Get payer mix reimbursement data for a provider (V2 endpoint)
     * Returns reimbursement information grouped by code with payer details
     */
    getPayerMixReimbursementV2: builder.query<Record<string, any>, GetPayerMixReimbursementParams>(
      {
        query: ({ providerId, queryParams }) => ({
          url: `v1/entities/entity/${providerId}/reimbursement/${queryParams}`,
          method: 'GET',
        }),
        providesTags: (result, error, { providerId, queryParams }) => [
          { type: PAYER_MIX_REIMBURSEMENT_TAG, id: `${providerId}-${queryParams}` },
        ],
      }
    ),
  }),
});

export const { useGetPayerMixV2Query, useGetPayerMixReimbursementV2Query } =
  payerMixApi;
